import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTimezone } from '@/contexts/TimezoneContext';
import { Tooltip } from '@/components/ui/Tooltip';
import { Select } from '@/components/ui/Select';
import { useAnalyticsStore } from '@/stores/analyticsStore';
import { AnalyticsHubRow } from '@/types/analytics';
import { formatCompactNumber, formatPercent } from '@/utils/formatNumber';
import { PLATFORMS } from '@/components/ui/PlatformSelect';

/**
 * AnalyticsTable Component
 *
 * Data table displaying analytics data with:
 * - Sortable columns (Reach, Engagement, Eng. Rate, Published)
 * - Expandable engagement breakdown
 * - Row click to open detail drawer
 * - Pagination controls
 */

type SortByField = 'publishedAt' | 'reach' | 'impressions' | 'engagement' | 'engagementRate' | 'likes' | 'comments' | 'shares';

interface AnalyticsTableProps {
  data: AnalyticsHubRow[];
  pagination: { page: number; limit: number; total: number; pages: number };
  isLoading: boolean;
  isFetching: boolean;
}

interface SortHeaderProps {
  column: SortByField;
  label: string;
}

export const AnalyticsTable = ({
  data,
  pagination,
  isLoading,
  isFetching,
}: AnalyticsTableProps) => {
  const { t } = useTranslation('analytics');
  const { formatRelativeDate, formatDateTime } = useTimezone();
  const store = useAnalyticsStore();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Toggle engagement breakdown
  const toggleExpand = (id: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger row click
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Sort header component
  const SortHeader = ({ column, label, tooltip, hideOnMobile, hideOnTablet }: SortHeaderProps & { tooltip?: string; hideOnMobile?: boolean; hideOnTablet?: boolean }) => {
    const isActive = store.sortBy === column;
    const hideClasses = hideOnMobile ? 'hidden sm:table-cell' : hideOnTablet ? 'hidden md:table-cell' : '';
    return (
      <th
        className={`px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50 select-none ${hideClasses}`}
        onClick={() => store.toggleSort(column)}
      >
        <span className="flex items-center gap-1">
          {label}
          {tooltip && (
            <Tooltip content={tooltip} side="bottom">
              <i className="hm hm-info text-gray-400 text-[10px] cursor-help" onClick={(e) => e.stopPropagation()} />
            </Tooltip>
          )}
          {isActive && (
            <span className="text-primary-500">
              {store.sortOrder === 'asc' ? '↑' : '↓'}
            </span>
          )}
        </span>
      </th>
    );
  };

  // Get platform info
  const getPlatformInfo = (platform: string) => {
    return PLATFORMS.find((p) => p.value === platform);
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg sm:border sm:border-gray-200 sm:overflow-hidden">
        {/* Desktop skeleton */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('col_post', { defaultValue: 'Post' })}
                </th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('col_channel', { defaultValue: 'Channel' })}
                </th>
                <th className="hidden sm:table-cell px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('col_published', { defaultValue: 'Published' })}
                </th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('col_reach_impr', { defaultValue: 'Reach / Impr.' })}
                </th>
                <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('col_engagement', { defaultValue: 'Engagement' })}
                </th>
                <th className="hidden md:table-cell px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('col_eng_rate', { defaultValue: 'Eng. Rate' })}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i}>
                  <td className="px-2 sm:px-4 py-3 sm:py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded animate-pulse" />
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
                      </div>
                    </div>
                  </td>
                  <td className="px-2 sm:px-4 py-3 sm:py-4">
                    <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse" />
                  </td>
                  <td className="hidden sm:table-cell px-2 sm:px-4 py-3 sm:py-4">
                    <div className="h-4 bg-gray-200 rounded w-16 animate-pulse" />
                  </td>
                  <td className="px-2 sm:px-4 py-3 sm:py-4">
                    <div className="h-4 bg-gray-200 rounded w-12 animate-pulse" />
                  </td>
                  <td className="px-2 sm:px-4 py-3 sm:py-4">
                    <div className="h-4 bg-gray-200 rounded w-12 animate-pulse" />
                  </td>
                  <td className="hidden md:table-cell px-2 sm:px-4 py-3 sm:py-4">
                    <div className="h-4 bg-gray-200 rounded w-12 animate-pulse" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile skeleton */}
        <div className="sm:hidden divide-y divide-gray-200">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gray-200 rounded animate-pulse flex-shrink-0" />
                  <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse flex-shrink-0" />
                </div>
                <div className="h-3 bg-gray-200 rounded w-14 animate-pulse" />
              </div>
              <div className="h-3 bg-gray-200 rounded w-full animate-pulse" />
              <div className="flex gap-2">
                <div className="h-5 bg-gray-200 rounded w-16 animate-pulse" />
                <div className="h-5 bg-gray-200 rounded w-14 animate-pulse" />
                <div className="h-5 bg-gray-200 rounded w-12 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 py-20">
        <div className="text-center text-gray-400">
          <i className="hm hm-graph text-4xl mb-3 block" />
          <p className="text-base font-medium">
            {t('empty_state', { defaultValue: 'No analytics data' })}
          </p>
          <p className="text-sm mt-1">
            {t('empty_state_subtitle', {
              defaultValue: 'Publish some posts to see analytics',
            })}
          </p>
        </div>
      </div>
    );
  }

  // Calculate pagination display
  const startItem = (pagination.page - 1) * pagination.limit + 1;
  const endItem = Math.min(pagination.page * pagination.limit, pagination.total);

  const perPageOptions = [
    { value: '10', label: '10 ' + t('per_page', { defaultValue: 'per page' }) },
    { value: '20', label: '20 ' + t('per_page', { defaultValue: 'per page' }) },
    { value: '50', label: '50 ' + t('per_page', { defaultValue: 'per page' }) },
  ];

  return (
    <div className="bg-white rounded-lg sm:border sm:border-gray-200 sm:overflow-hidden">
      {/* Fetching indicator */}
      {isFetching && !isLoading && (
        <div className="h-1 bg-primary-500 animate-pulse" />
      )}

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('col_post', { defaultValue: 'Post' })}
              </th>
              <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('col_channel', { defaultValue: 'Channel' })}
              </th>
              <SortHeader
                column="publishedAt"
                label={t('col_published', { defaultValue: 'Published' })}
                hideOnMobile
              />
              <SortHeader
                column="reach"
                label={t('col_reach_impr', { defaultValue: 'Reach / Impr.' })}
                tooltip={t('tip_reach_col', { defaultValue: 'Unique accounts reached, or total impressions for platforms where reach is unavailable (YouTube, Pinterest, Bluesky)' })}
              />
              <SortHeader
                column="engagement"
                label={t('col_engagement', { defaultValue: 'Engagement' })}
                tooltip={t('tip_engagement_col', { defaultValue: 'Likes + comments + shares + saves. Click a row to expand' })}
              />
              <SortHeader
                column="engagementRate"
                label={t('col_eng_rate', { defaultValue: 'Eng. Rate' })}
                tooltip={t('tip_eng_rate_col', { defaultValue: 'Engagement divided by reach (or impressions when reach is unavailable)' })}
                hideOnTablet
              />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row) => {
              const platformInfo = getPlatformInfo(row.platform);
              const isExpanded = expandedRows.has(row.channelInstanceId);

              return (
                <Fragment key={row.channelInstanceId}>
                  {/* Main Row */}
                  <tr
                    onClick={() => store.setSelectedChannelInstanceId(row.channelInstanceId)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    {/* Post */}
                    <td className="px-2 sm:px-4 py-2 sm:py-4">
                      <div className="flex items-center gap-3">
                        {row.thumbnailUrl ? (
                          <img
                            src={row.thumbnailUrl}
                            alt=""
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded object-cover flex-shrink-0"
                          />
                        ) : platformInfo ? (
                          <div
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: platformInfo.color }}
                          >
                            <span className="text-white text-xs font-bold">
                              {row.platform.charAt(0)}
                            </span>
                          </div>
                        ) : (
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded bg-gray-200 flex-shrink-0" />
                        )}
                        <Tooltip content={row.postCaption || t('no_caption', { defaultValue: '(No caption)' })} side="bottom">
                          <p className="text-xs sm:text-sm text-gray-900 line-clamp-2 flex-1 max-w-[120px] sm:max-w-[200px] lg:max-w-[300px]">
                            {row.postCaption || t('no_caption', { defaultValue: '(No caption)' })}
                          </p>
                        </Tooltip>
                      </div>
                    </td>

                    {/* Channel */}
                    <td className="px-2 sm:px-4 py-2 sm:py-4">
                      <Tooltip
                        content={
                          <div className="text-xs">
                            <p className="font-medium">{row.platformUsername || '—'}</p>
                            {platformInfo && <p className="text-gray-400">{platformInfo.label}</p>}
                            {row.externalUrl && <p className="text-primary-400 mt-0.5">{t('click_to_view', { defaultValue: 'Click to view post' })}</p>}
                          </div>
                        }
                        side="bottom"
                      >
                        <div
                          className={`relative flex-shrink-0 w-8 h-8 ${row.externalUrl ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                          onClick={(e) => {
                            if (row.externalUrl) {
                              e.stopPropagation();
                              window.open(row.externalUrl, '_blank', 'noopener,noreferrer');
                            }
                          }}
                        >
                          {row.channelAvatarUrl ? (
                            <>
                              <img
                                src={row.channelAvatarUrl}
                                alt=""
                                className="w-8 h-8 rounded-full object-cover"
                              />
                              {/* Platform badge overlay */}
                              {platformInfo && (
                                <span
                                  className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white"
                                  style={{ backgroundColor: platformInfo.color }}
                                >
                                  <FontAwesomeIcon
                                    icon={platformInfo.icon}
                                    className="text-white text-[7px]"
                                  />
                                </span>
                              )}
                            </>
                          ) : platformInfo ? (
                            <span
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: platformInfo.color }}
                            >
                              <FontAwesomeIcon
                                icon={platformInfo.icon}
                                className="text-white text-sm"
                              />
                            </span>
                          ) : (
                            <span className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-400 text-xs">?</span>
                            </span>
                          )}
                        </div>
                      </Tooltip>
                    </td>

                    {/* Published */}
                    <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-4">
                      <Tooltip content={formatDateTime(row.publishedAt)} side="bottom">
                        <span className="text-xs sm:text-sm text-gray-600 cursor-help">
                          {formatRelativeDate(row.publishedAt)}
                        </span>
                      </Tooltip>
                    </td>

                    {/* Reach / Impressions */}
                    <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm text-gray-900 font-medium">
                      <Tooltip
                        content={
                          row.reach > 0
                            ? t('reach_value', { defaultValue: 'Reach: {{value}}', value: row.reach.toLocaleString() })
                            : row.impressions > 0
                              ? t('impressions_value', { defaultValue: 'Impressions: {{value}} (reach not available for this platform)', value: row.impressions.toLocaleString() })
                              : t('no_reach_data', { defaultValue: 'No reach or impression data' })
                        }
                        side="bottom"
                      >
                        <span className="cursor-help">
                          {row.reach === 0 && row.impressions === 0
                            ? '—'
                            : (
                              <>
                                {formatCompactNumber(row.reach > 0 ? row.reach : row.impressions)}
                                {row.reach === 0 && row.impressions > 0 && (
                                  <span className="text-[10px] text-gray-400 ml-0.5" title="Impressions">i</span>
                                )}
                              </>
                            )
                          }
                        </span>
                      </Tooltip>
                    </td>

                    {/* Engagement */}
                    <td
                      className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm text-gray-900 font-medium"
                      onClick={(e) => toggleExpand(row.channelInstanceId, e)}
                    >
                      <span className="flex items-center gap-1 cursor-pointer hover:text-primary-600">
                        {formatCompactNumber(row.engagement)}
                        <i
                          className={`hm hm-chevron-${isExpanded ? 'up' : 'down'} text-xs text-gray-400`}
                        />
                      </span>
                    </td>

                    {/* Engagement Rate */}
                    <td className="hidden md:table-cell px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm text-gray-900 font-medium">
                      {row.reach === 0 && row.impressions === 0
                        ? <span className="text-gray-400">N/A</span>
                        : formatPercent(row.engagementRate)
                      }
                    </td>
                  </tr>

                  {/* Expanded Engagement Breakdown */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={6} className="px-2 sm:px-4 py-3 bg-gray-50">
                        <div className="flex flex-wrap gap-2 sm:gap-4 text-xs text-gray-600">
                          <div className="flex items-center gap-1">
                            <i className="hm hm-heart text-red-500" />
                            <span className="font-medium">{formatCompactNumber(row.likes)}</span>
                            <span>{t('likes', { defaultValue: 'likes' })}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <i className="hm hm-chat-bubble text-primary-700" />
                            <span className="font-medium">{formatCompactNumber(row.comments)}</span>
                            <span>{t('comments', { defaultValue: 'comments' })}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <i className="hm hm-share text-green-500" />
                            <span className="font-medium">{formatCompactNumber(row.shares)}</span>
                            <span>{t('shares', { defaultValue: 'shares' })}</span>
                          </div>
                          {row.saves > 0 && (
                            <div className="flex items-center gap-1">
                              <i className="hm hm-star text-yellow-500" />
                              <span className="font-medium">{formatCompactNumber(row.saves)}</span>
                              <span>{t('saves', { defaultValue: 'saves' })}</span>
                            </div>
                          )}
                          {row.clicks > 0 && (
                            <div className="flex items-center gap-1">
                              <i className="hm hm-cursor text-secondary-500" />
                              <span className="font-medium">{formatCompactNumber(row.clicks)}</span>
                              <span>{t('clicks', { defaultValue: 'clicks' })}</span>
                            </div>
                          )}
                          {(() => {
                            const knownTotal = row.likes + row.comments + row.shares + row.saves + row.clicks;
                            const other = row.engagement - knownTotal;
                            if (other > 0) {
                              return (
                                <div className="flex items-center gap-1">
                                  <i className="hm hm-ellipsis-horizontal text-gray-400" />
                                  <span className="font-medium">{formatCompactNumber(other)}</span>
                                  <span>{t('other', { defaultValue: 'other' })}</span>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="sm:hidden py-2">
        {data.map((row) => {
          const platformInfo = getPlatformInfo(row.platform);
          const isExpanded = expandedRows.has(row.channelInstanceId);
          const reachValue = row.reach > 0 ? row.reach : row.impressions;
          const usesImpressions = row.reach === 0 && row.impressions > 0;

          return (
            <div
              key={row.channelInstanceId}
              className="border border-gray-200 rounded-lg p-3 space-y-2 mx-3 my-2 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => store.setSelectedChannelInstanceId(row.channelInstanceId)}
            >
              {/* Row 1: thumbnail + channel avatar+badge + date */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {/* Post thumbnail */}
                  {row.thumbnailUrl ? (
                    <img
                      src={row.thumbnailUrl}
                      alt=""
                      className="w-8 h-8 rounded object-cover flex-shrink-0"
                    />
                  ) : platformInfo ? (
                    <div
                      className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: platformInfo.color }}
                    >
                      <span className="text-white text-xs font-bold">
                        {row.platform.charAt(0)}
                      </span>
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded bg-gray-200 flex-shrink-0" />
                  )}

                  {/* Channel avatar with platform badge */}
                  <div
                    className={`relative flex-shrink-0 w-8 h-8 ${row.externalUrl ? 'cursor-pointer' : ''}`}
                    onClick={(e) => {
                      if (row.externalUrl) {
                        e.stopPropagation();
                        window.open(row.externalUrl, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    {row.channelAvatarUrl ? (
                      <>
                        <img
                          src={row.channelAvatarUrl}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        {platformInfo && (
                          <span
                            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white"
                            style={{ backgroundColor: platformInfo.color }}
                          >
                            <FontAwesomeIcon
                              icon={platformInfo.icon}
                              className="text-white text-[7px]"
                            />
                          </span>
                        )}
                      </>
                    ) : platformInfo ? (
                      <span
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: platformInfo.color }}
                      >
                        <FontAwesomeIcon
                          icon={platformInfo.icon}
                          className="text-white text-sm"
                        />
                      </span>
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-xs">?</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Relative date */}
                <Tooltip content={formatDateTime(row.publishedAt)} side="bottom">
                  <span className="text-xs text-gray-400 flex-shrink-0 cursor-help">
                    {formatRelativeDate(row.publishedAt)}
                  </span>
                </Tooltip>
              </div>

              {/* Row 2: caption */}
              {row.postCaption ? (
                <p className="text-xs text-gray-700 truncate">
                  {row.postCaption}
                </p>
              ) : (
                <p className="text-xs text-gray-400 italic">
                  {t('no_caption', { defaultValue: '(No caption)' })}
                </p>
              )}

              {/* Row 3: stat badges */}
              <div className="flex flex-wrap items-center gap-1.5">
                {/* Reach / Impressions */}
                <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5 font-medium">
                  <i className="hm hm-graph text-[10px] text-gray-500" />
                  {reachValue === 0 ? '—' : (
                    <>
                      {formatCompactNumber(reachValue)}
                      {usesImpressions && (
                        <span className="text-[9px] text-gray-400">i</span>
                      )}
                    </>
                  )}
                </span>

                {/* Engagement — tappable to expand */}
                <span
                  className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5 font-medium cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={(e) => toggleExpand(row.channelInstanceId, e)}
                >
                  <i className="hm hm-heart text-[10px] text-red-400" />
                  {formatCompactNumber(row.engagement)}
                  <i className={`hm hm-chevron-${isExpanded ? 'up' : 'down'} text-[9px] text-gray-400`} />
                </span>

                {/* Engagement Rate */}
                <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5 font-medium">
                  <i className="hm hm-arrow-up text-[10px] text-primary-500" />
                  {row.reach === 0 && row.impressions === 0
                    ? <span className="text-gray-400">N/A</span>
                    : formatPercent(row.engagementRate)
                  }
                </span>
              </div>

              {/* Expanded engagement breakdown */}
              {isExpanded && (
                <div className="flex flex-wrap gap-2 pt-1.5 border-t border-gray-100 text-xs text-gray-600">
                  <div className="flex items-center gap-1">
                    <i className="hm hm-heart text-red-500" />
                    <span className="font-medium">{formatCompactNumber(row.likes)}</span>
                    <span>{t('likes', { defaultValue: 'likes' })}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="hm hm-chat-bubble text-primary-700" />
                    <span className="font-medium">{formatCompactNumber(row.comments)}</span>
                    <span>{t('comments', { defaultValue: 'comments' })}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="hm hm-share text-green-500" />
                    <span className="font-medium">{formatCompactNumber(row.shares)}</span>
                    <span>{t('shares', { defaultValue: 'shares' })}</span>
                  </div>
                  {row.saves > 0 && (
                    <div className="flex items-center gap-1">
                      <i className="hm hm-star text-yellow-500" />
                      <span className="font-medium">{formatCompactNumber(row.saves)}</span>
                      <span>{t('saves', { defaultValue: 'saves' })}</span>
                    </div>
                  )}
                  {row.clicks > 0 && (
                    <div className="flex items-center gap-1">
                      <i className="hm hm-cursor text-secondary-500" />
                      <span className="font-medium">{formatCompactNumber(row.clicks)}</span>
                      <span>{t('clicks', { defaultValue: 'clicks' })}</span>
                    </div>
                  )}
                  {(() => {
                    const knownTotal = row.likes + row.comments + row.shares + row.saves + row.clicks;
                    const other = row.engagement - knownTotal;
                    if (other > 0) {
                      return (
                        <div className="flex items-center gap-1">
                          <i className="hm hm-ellipsis-horizontal text-gray-400" />
                          <span className="font-medium">{formatCompactNumber(other)}</span>
                          <span>{t('other', { defaultValue: 'other' })}</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination Footer */}
      <div className="px-3 sm:px-4 py-2.5 bg-gray-50 border-t border-gray-200">
        {/* Mobile: stacked */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          {/* Row 1 on mobile: Prev + Page + Next centered */}
          <div className="flex items-center justify-center sm:justify-start gap-2">
            <button
              onClick={() => store.setPage(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-2.5 py-1 text-xs border border-gray-200 rounded-md bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {t('prev', { defaultValue: 'Prev' })}
            </button>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {pagination.page} / {pagination.pages}
            </span>
            <button
              onClick={() => store.setPage(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="px-2.5 py-1 text-xs border border-gray-200 rounded-md bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {t('next', { defaultValue: 'Next' })}
            </button>
          </div>

          {/* Row 2 on mobile: Showing count + per page */}
          <div className="flex items-center justify-between sm:justify-end gap-3">
            <span className="text-[10px] sm:text-xs text-gray-400">
              {t('showing_items', {
                defaultValue: 'Showing {{start}}-{{end}} of {{total}}',
                start: startItem,
                end: endItem,
                total: pagination.total,
              })}
            </span>
            <Select
              options={perPageOptions}
              value={String(pagination.limit)}
              onValueChange={(value) => store.setLimit(Number(value))}
              size="sm"
              className="w-[110px] sm:w-[130px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
